import { useState } from "react";
import { CheckCircle2, Loader2, X, Users, Truck, Receipt, ShoppingCart } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

export const QUICK_ACTIONS = [
  ["presenza", "Aggiungi presenza", Users],
  ["ddt", "Aggiungi DDT / bolla", Truck],
  ["acquisto", "Aggiungi acquisto", ShoppingCart],
  ["fattura", "Aggiungi fattura", Receipt],
];
export const COST_LABEL = { ddt: "DDT / bolla", acquisto: "Acquisto", fattura: "Fattura" };
export const today = () => new Date().toISOString().slice(0, 10);

export const buildToolFields = (tool, text) => {
  const fields = {};
  let used = false;
  const inputs = tool?.spec?.inputs || [];
  const textual = inputs.filter((i) => ["textarea", "text", "audio"].includes(i.type));
  inputs.forEach((i) => {
    if (i.type === "date") fields[i.key] = today();
    else if (textual.includes(i) && !used && text) { fields[i.key] = text; used = true; }
  });
  return fields;
};

export const toolAcceptsText = (t) => !(t.spec?.inputs || []).some((i) => i.type === "file" && i.required) && (t.spec?.inputs || []).some((i) => ["textarea", "text", "audio"].includes(i.type));
export const toolAcceptsFile = (t) => (t.spec?.inputs || []).some((i) => i.type === "file");

const Field = ({ label, children, className = "" }) => (
  <label className={`text-xs text-slate-500 flex flex-col gap-0.5 ${className}`}>{label}{children}</label>
);
const inp = "border border-slate-200 rounded-lg px-2 py-1.5 text-sm text-slate-800 bg-white";

export const AttendanceDraft = ({ siteId, draft, onSaved, onCancel }) => {
  const [rows, setRows] = useState(draft.entries.map((e) => ({ ...e, on: true })));
  const [date, setDate] = useState(draft.date || today());
  const [saving, setSaving] = useState(false);
  const upd = (i, patch) => setRows(rows.map((r, k) => (k === i ? { ...r, ...patch } : r)));
  const save = async () => {
    const entries = rows.filter((r) => r.on).map((r) => ({ worker_id: r.worker_id, site_id: siteId, date, hours: Number(r.hours) || 0, note: r.note || "", method: draft.method || "voce" }));
    if (!entries.length) return;
    setSaving(true);
    try { await api.saveAttendanceBatch(siteId, date, entries); toast.success(`${entries.length} presenze registrate`); onSaved?.({ kind: "presenza", count: entries.length }); }
    catch (e) { toast.error(e?.response?.data?.detail || "Salvataggio non riuscito"); }
    finally { setSaving(false); }
  };
  return (
    <div className="bg-white text-slate-800 rounded-lg p-3 space-y-3" data-testid="attendance-draft">
      <div className="flex items-center gap-2 text-sm">
        <Users className="w-4 h-4 text-orange-600" /><span className="font-semibold">Presenze da registrare</span>
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="attendance-draft-date" className="text-xs border border-slate-200 rounded px-1.5 py-1 ml-1" />
        <button onClick={onCancel} className="ml-auto p-1 text-slate-400 hover:text-slate-700" data-testid="attendance-draft-cancel"><X className="w-4 h-4" /></button>
      </div>
      {rows.length === 0 && <p className="text-xs text-slate-500">Nessun operaio riconosciuto nel testo/foto.</p>}
      <ul className="space-y-1.5">
        {rows.map((r, i) => (
          <li key={r.worker_id} className="flex flex-wrap items-center gap-2 text-sm border border-slate-100 rounded-lg px-2 py-1.5" data-testid={`attendance-draft-row-${i}`}>
            <input type="checkbox" checked={r.on} onChange={(e) => upd(i, { on: e.target.checked })} className="accent-orange-600" />
            <span className="flex-1 min-w-[140px] font-medium">{r.name}<span className="text-xs text-slate-400 font-normal ml-1">{r.role}{r.confidence != null ? ` · ${Math.round(r.confidence * 100)}%` : ""}</span></span>
            <input type="number" min="0" max="24" step="0.5" value={r.hours} onChange={(e) => upd(i, { hours: e.target.value })} className="w-16 text-xs border border-slate-200 rounded px-1.5 py-1 font-mono" /> <span className="text-xs text-slate-400">ore</span>
          </li>
        ))}
      </ul>
      {draft.unmatched?.length > 0 && <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">Non in anagrafica: {draft.unmatched.join(", ")}. Aggiungili nella pagina Operai.</p>}
      <div className="flex justify-end">
        <button onClick={save} disabled={saving || !rows.some((r) => r.on)} data-testid="attendance-draft-save" className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-lg bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Registra presenze
        </button>
      </div>
    </div>
  );
};

export const CostDraft = ({ siteId, draft, onSaved, onCancel }) => {
  const [c, setC] = useState({ ...draft, date: draft.date || today() });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setC({ ...c, [k]: e.target.value });
  const save = async () => {
    setSaving(true);
    try {
      const body = { ...c, quantity: c.quantity === "" || c.quantity == null ? null : Number(c.quantity), amount: c.amount === "" || c.amount == null ? null : Number(c.amount) };
      delete body.confidence;
      await api.createCost(siteId, body);
      toast.success(`${COST_LABEL[c.kind]} registrato nel cantiere`);
      onSaved?.({ kind: c.kind, amount: body.amount });
    } catch (e) { toast.error(e?.response?.data?.detail || "Salvataggio non riuscito"); }
    finally { setSaving(false); }
  };
  return (
    <div className="bg-white text-slate-800 rounded-lg p-3 space-y-3" data-testid="cost-draft">
      <div className="flex items-center gap-2 text-sm">
        <Receipt className="w-4 h-4 text-orange-600" /><span className="font-semibold">{COST_LABEL[c.kind]} — controlla e salva</span>
        {c.confidence > 0 && <span className="text-[11px] text-slate-400">letto {Math.round(c.confidence * 100)}%</span>}
        <button onClick={onCancel} className="ml-auto p-1 text-slate-400 hover:text-slate-700" data-testid="cost-draft-cancel"><X className="w-4 h-4" /></button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        <Field label="Fornitore" className="col-span-2 sm:col-span-1"><input value={c.supplier} onChange={set("supplier")} data-testid="cost-draft-supplier" className={inp} /></Field>
        <Field label="N. documento"><input value={c.ref} onChange={set("ref")} data-testid="cost-draft-ref" className={inp} /></Field>
        <Field label="Data"><input type="date" value={c.date} onChange={set("date")} data-testid="cost-draft-date" className={inp} /></Field>
        <Field label="Descrizione / materiale" className="col-span-2 sm:col-span-3"><input value={c.description} onChange={set("description")} data-testid="cost-draft-description" className={inp} /></Field>
        <Field label="Quantità"><input type="number" step="any" value={c.quantity ?? ""} onChange={set("quantity")} data-testid="cost-draft-quantity" className={`${inp} font-mono`} /></Field>
        <Field label="U.M."><input value={c.unit} onChange={set("unit")} data-testid="cost-draft-unit" className={inp} /></Field>
        <Field label="Importo €"><input type="number" step="0.01" value={c.amount ?? ""} onChange={set("amount")} data-testid="cost-draft-amount" className={`${inp} font-mono font-semibold`} /></Field>
      </div>
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] text-slate-400">Va nel Registro rapido del cantiere e nel cruscotto Contabile{c.file_ids?.length ? ` · ${c.file_ids.length} foto archiviate in Contabilità` : ""}.</span>
        <button onClick={save} disabled={saving} data-testid="cost-draft-save" className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-lg bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Salva
        </button>
      </div>
    </div>
  );
};

export const ActionSelect = ({ value, onChange, tools, mode, testid }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="border border-slate-200 rounded-lg px-2 py-2 text-sm text-slate-800 bg-white w-full">
    {mode === "voice" ? <option value="task">Task / nota (l'AI trova il cantiere)</option> : <option value="archive">Solo archiviare in Documenti › Foto</option>}
    {QUICK_ACTIONS.map(([k, l]) => <option key={k} value={k}>{l}{k === "presenza" && mode === "photo" ? " (riconoscimento volti)" : ""}</option>)}
    {tools.length > 0 && <optgroup label="Esegui tool del cantiere">{tools.map((t) => <option key={t.id} value={`tool:${t.id}`}>{t.name}{t.section === "contabile" ? " (contabile)" : ""}</option>)}</optgroup>}
  </select>
);
